import { httpsCallable } from 'firebase/functions';
import { functions } from '@/lib/firebase';

export interface Newsletter {
  id: string;
  email: string;
  isActive: boolean;
  subscribedAt: Date;
  unsubscribedAt?: Date;
}

export interface NewsletterSubscriptionData {
  email: string;
  source?: string; // Track where the subscription came from
}

/**
 * Newsletter Service
 * Handles newsletter subscriptions via Firebase Cloud Functions
 */
export class NewsletterService {

  /**
   * Subscribe to newsletter via cloud function
   * Backend handles validation, duplicate prevention, and email sending
   */
  static async subscribe(data: NewsletterSubscriptionData): Promise<{ success: boolean; message: string }> {
    try {
      const { email, source = 'landing-page' } = data;

      // Basic email validation on frontend
      if (!this.isValidEmail(email)) {
        return {
          success: false,
          message: 'Please enter a valid email address.'
        };
      }

      // Call cloud function to handle subscription
      const subscribeToNewsletter = httpsCallable(functions, 'subscribeToNewsletter');
      
      const result = await subscribeToNewsletter({
        email: email.toLowerCase().trim(),
        source
      });

      const response = result.data as { success: boolean; message: string };
      
      return response;

    } catch (error: any) {
      console.error('Error subscribing to newsletter:', error);
      
      // Handle specific Firebase errors
      if (error.code === 'functions/already-exists') {
        return {
          success: false,
          message: 'This email is already subscribed to our newsletter.'
        };
      }
      
      if (error.code === 'functions/invalid-argument') {
        return {
          success: false,
          message: 'Please enter a valid email address.'
        };
      }
      
      return {
        success: false,
        message: 'Something went wrong. Please try again later.'
      };
    }
  }

  /**
   * Unsubscribe from newsletter via cloud function
   */
  static async unsubscribe(email: string): Promise<{ success: boolean; message: string }> {
    try {
      // Call cloud function to handle unsubscription
      const unsubscribeFromNewsletter = httpsCallable(functions, 'unsubscribeFromNewsletter');
      
      const result = await unsubscribeFromNewsletter({
        email: email.toLowerCase().trim()
      });

      const response = result.data as { success: boolean; message: string };
      
      return response;

    } catch (error: any) {
      console.error('Error unsubscribing from newsletter:', error);
      
      return {
        success: false,
        message: 'Something went wrong. Please try again later.'
      };
    }
  }

  /**
   * Update newsletter preferences via cloud function
   */
  static async updatePreferences(
    email: string, 
    preferences: { frequency?: 'weekly' | 'monthly'; categories?: string[] }
  ): Promise<{ success: boolean; message: string }> {
    try {
      // Call cloud function to handle preference updates
      const updateNewsletterPreferences = httpsCallable(functions, 'updateNewsletterPreferences');
      
      const result = await updateNewsletterPreferences({
        email: email.toLowerCase().trim(),
        preferences
      });

      const response = result.data as { success: boolean; message: string };
      
      return response;

    } catch (error: any) {
      console.error('Error updating newsletter preferences:', error);
      
      return {
        success: false,
        message: 'Something went wrong. Please try again later.'
      };
    }
  }

  /**
   * Validate email format (frontend validation)
   */
  private static isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }
} 